import type { Granularity } from "./api/types";

export type RangeKey = "7d" | "30d" | "90d" | "all";

export const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "all", label: "All" },
];

const RANGE_DAYS: Record<Exclude<RangeKey, "all">, number> = { "7d": 7, "30d": 30, "90d": 90 };

const DAY_MS = 24 * 60 * 60 * 1000;

export interface RangeWindow {
  /** ISO start of the current window (undefined for "all" → server picks earliest). */
  from?: string;
  to: string;
  granularity: Granularity;
  /** Length in days, or null for "all". */
  days: number | null;
  /** ISO start of the previous equal-length window (for KPI deltas). */
  prevFrom?: string;
  /** YYYY-MM-DD boundary separating previous vs current buckets. */
  currentFromYmd?: string;
}

/** Resolve a range selector key into query windows for chart + KPI deltas. */
export function resolveRange(range: RangeKey, now: number = Date.now()): RangeWindow {
  const to = new Date(now);
  if (range === "all") {
    return { to: to.toISOString(), granularity: "week", days: null };
  }
  const days = RANGE_DAYS[range];
  const from = new Date(now - days * DAY_MS);
  const prevFrom = new Date(now - 2 * days * DAY_MS);
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    // PRD §5.3: auto-roll to weekly only when range > 90 days (so 90d stays daily).
    granularity: days > 90 ? "week" : "day",
    days,
    prevFrom: prevFrom.toISOString(),
    currentFromYmd: from.toISOString().slice(0, 10),
  };
}

export function ymdDaysAgo(days: number, now: number = Date.now()): string {
  return new Date(now - days * DAY_MS).toISOString().slice(0, 10);
}
